import { selectedFields } from '@immosky/shared';
import { FilterState } from '@immosky/types';
import { MINIMUM_RADIUS } from '@immosky/object-search-filter';

export class RealEstateSearchQueryBuilder {
  private filterState!: FilterState;
  private sort: any[] = [];
  private bool: {
    must: any[];
    must_not: any[];
    should: any[];
    minimum_should_match: number;
    filter: any[];
  } = {
    must: [],
    must_not: [
      {
        term: { status: 'sold' },
      },
    ],
    should: [],
    minimum_should_match: 0,
    filter: [],
  };

  setFilterState(
    filterState: FilterState,
    region_index: string
  ): RealEstateSearchQueryBuilder {
    this.filterState = filterState;
    this.appendCountries();
    this.appendIsResidia();
    this.appendRegion(region_index);
    this.appendContractType();
    this.appendMainCategory();
    this.appendSubCategories();
    this.appendPrice();
    this.appendRoomCount();
    this.appendLivingSpace();
    this.appendUsageSpace();
    this.appendPropertySpace();
    this.appendSort();

    return this;
  }

  private appendIsResidia() {
    const filter = {
      term: {
        isResidia: true,
      },
    };
    if(this.filterState.isResidia) {
      this.bool.must.push(filter);
    } else {
      this.bool.must_not.push(filter);
    }
  }

  private appendCountries() {
    const filter = {
      terms: {
        countryIsocode: this.filterState.countries,
      },
    };

    this.bool.must.push(filter);
  }

  private appendRegion(region_index: string) {
    const regionFilter = this.filterState.region;
    const radiusFilter = this.filterState.radius;
    const regions = regionFilter.regions;

    if (regionFilter.isActive) {
      for (const region of regions) {
        let filter;

        if (
          (regions.length === 1 && radiusFilter.isActive) ||
          region.geometry.type === 'Point'
        ) {
          let radius = MINIMUM_RADIUS;
          if (regions.length === 1) {
            radius = radiusFilter.filter.value;
          }

          filter = {
            geo_distance: {
              distance: `${radius}km`, // distance can't be zero
              location: {
                lat: region.centroid.lat,
                lon: region.centroid.lng,
              },
            },
          };
        } else if (
          region.geometry.type === 'Polygon' ||
          region.geometry.type === 'MultiPolygon'
        ) {
          filter = {
            geo_shape: {
              location: {
                indexed_shape: {
                  index: region_index,
                  id: `${region.id}`,
                  path: 'geometry',
                },
              },
            },
          };
        }

        this.bool.should.push(filter);
      }

      this.bool.minimum_should_match = 1;
    }
  }

  private appendContractType() {
    const filter = {
      term: {
        contractType: this.filterState.contractType.filter.value,
      },
    };

    this.bool.must.push(filter);
  }

  private appendMainCategory() {
    const categoryFilter = this.filterState.category.main;

    if (categoryFilter.isActive) {
      const filter = {
        term: {
          category: categoryFilter.filter.value,
        },
      };
      this.bool.must.push(filter);
    }
  }

  private appendSubCategories() {
    const mainCategoryFilter = this.filterState.category.main;
    const subcategoryFilter = this.filterState.category.sub;

    if (subcategoryFilter.isActive) {
      const subcategories =
        subcategoryFilter.filter[mainCategoryFilter.filter.value];
      const activeSubcategories = subcategories.filter((s) => s.isChecked);

      const filter = {
        terms: {
          subCategory: activeSubcategories.map((s) => s.value),
        },
      };

      this.bool.must.push(filter);
    }
  }

  private appendPrice() {
    const priceFilter = this.filterState.price;
    const contractType = this.filterState.contractType.filter.value;

    let priceProperty = 'price.total';
    if (contractType === 'buy') {
      priceProperty = 'price.total';
    } else if (contractType === 'rent') {
      priceProperty = 'price.rentGross';
    }

    if (priceFilter.isActive) {
      const min = priceFilter.filter.min.value;
      const max = priceFilter.filter.max.value;

      const filter: any = {
        nested: {
          path: 'price',
          query: {
            range: {
              [priceProperty]: {
                gte: min,
              },
            },
          },
        },
      };

      if (max !== Infinity) {
        filter.nested.query.range[priceProperty].lte = max;
      }

      this.bool.must.push(filter);
    }
  }

  private appendRoomCount() {
    const roomCountFilter = this.filterState.roomCount;

    if (roomCountFilter.isActive && roomCountFilter.isDisplayed) {
      const min = roomCountFilter.filter.min.value;
      const max = roomCountFilter.filter.max.value;

      const filter: any = {
        range: {
          rooms: {
            gte: min === Infinity ? 7 : min,
          },
        },
      };

      if (max !== Infinity) {
        filter.range.rooms.lte = max;
      }

      this.bool.must.push(filter);
    }
  }

  private appendLivingSpace() {
    const livingSpaceFilter = this.filterState.livingSpace;

    if (livingSpaceFilter.isActive && livingSpaceFilter.isDisplayed) {
      const min = livingSpaceFilter.filter.min.value;
      const max = livingSpaceFilter.filter.max.value;

      const filter: any = {
        nested: {
          path: 'space',
          query: {
            range: {
              'space.living': {
                gte: min,
              },
            },
          },
        },
      };

      if (max !== Infinity) {
        filter.nested.query.range['space.living'].lte = max;
      }

      this.bool.must.push(filter);
    }
  }

  private appendUsageSpace() {
    const usageSpaceFilter = this.filterState.usageSpace;

    if (usageSpaceFilter.isActive && usageSpaceFilter.isDisplayed) {
      const min = usageSpaceFilter.filter.min.value;
      const max = usageSpaceFilter.filter.max.value;

      const filter: any = {
        nested: {
          path: 'space',
          query: {
            range: {
              'space.usable': {
                gte: min,
              },
            },
          },
        },
      };

      if (max !== Infinity) {
        filter.nested.query.range['space.usable'].lte = max;
      }

      this.bool.must.push(filter);
    }
  }

  private appendPropertySpace() {
    const propertySpaceFilter = this.filterState.propertySpace;

    if (propertySpaceFilter.isActive && propertySpaceFilter.isDisplayed) {
      const min = propertySpaceFilter.filter.min.value;
      const max = propertySpaceFilter.filter.max.value;

      const filter: any = {
        nested: {
          path: 'space',
          query: {
            range: {
              'space.property': {
                gte: min,
              },
            },
          },
        },
      };

      if (max !== Infinity) {
        filter.nested.query.range['space.property'].lte = max;
      }

      this.bool.must.push(filter);
    }
  }

  private appendSort() {
    const sort = this.filterState.sort.sort.value;

    if (sort === 'lowest_price' || sort === 'highest_price') {
      this.appendPriceSort();
    } else if (sort === 'newest') {
      // todo implement newest sort
    }
  }

  private appendPriceSort() {
    const contractType = this.filterState.contractType.filter.value;
    const sort = this.filterState.sort.sort.value;

    let order = 'asc';
    if (sort === 'lowest_price') {
      order = 'asc';
    } else if (sort === 'highest_price') {
      order = 'desc';
    }

    let property = 'total';
    if (contractType === 'buy') {
      property = 'total';
    } else if (contractType === 'rent') {
      property = 'rentGross';
    }

    const priceSort = {
      [`price.${property}`]: {
        order,
        nested: {
          path: 'price',
        },
      },
    };

    this.sort.push(priceSort);
  }

  build(): any {
    return {
      from: 0,
      size: 1000,
      sort: this.sort.length > 0 ? this.sort : [{ publicationDate: 'desc' }],
      query: {
        bool: this.bool,
      },
      fields: selectedFields,
      _source: false,
    };
  }
}
